import api, { PaginatedApiResponse, ApiResponse } from './axiosInstance';
import { EquipmentListParams, CreateEquipmentRequest } from '../types/equipment';

export const amsEquipmentService = {
  async getEquipment(params?: EquipmentListParams) {
    const response = await api.get<PaginatedApiResponse<any>>('/ams/ams-equipment', { params });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch AMS equipment');
    return response.data;
  },

  async getById(equipmentId: string, includeCheckpoints?: boolean) {
    const path = includeCheckpoints ? `/ams/ams-equipment/${equipmentId}?includeCheckpoints=true` : `/ams/ams-equipment/${equipmentId}`;
    const response = await api.get<ApiResponse<any>>(path);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch AMS equipment');
    return response.data.data;
  },

  async getByIdWithCheckpointsPath(equipmentId: string) {
    const response = await api.get<ApiResponse<any>>(`/ams/ams-equipment/${equipmentId}/includeCheckpoints=true`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch AMS equipment');
    return response.data.data;
  },

  async createEquipment(payload: CreateEquipmentRequest) {
    const response = await api.post<ApiResponse<any>>('/ams/ams-equipment', payload);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to create AMS equipment');
    return response.data.data;
  },

  async updateEquipment(equipmentId: string, payload: Partial<CreateEquipmentRequest>) {
    const response = await api.patch<ApiResponse<any>>(`/ams/ams-equipment/${equipmentId}`, payload);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to update AMS equipment');
    return response.data.data;
  },

  async deleteEquipment(equipmentId: string) {
    const response = await api.delete<ApiResponse<null>>(`/ams/ams-equipment/${equipmentId}`);
    // Backend returns 204 No Content on successful deletion (no response body)
    // A successful response will have status 204 and no data
    if (response.status === 204 || response.status === 200) {
      return true;
    }
    // If there's a response body with success field, check it
    if (response.data && !response.data.success) {
      throw new Error(response.data.message || 'Failed to delete AMS equipment');
    }
    return true;
  },

  // Aliases for backward compatibility
  async create(payload: CreateEquipmentRequest) {
    return this.createEquipment(payload);
  },

  async update(equipmentId: string, payload: Partial<CreateEquipmentRequest>) {
    return this.updateEquipment(equipmentId, payload);
  },

  async remove(equipmentId: string) {
    return this.deleteEquipment(equipmentId);
  },

  async setCheckpointFrequency(equipmentId: string, checkpoint: string, frequency: string) {
    const response = await api.post<ApiResponse<any>>(`/ams/ams-equipment/${equipmentId}/frequency`, { checkpoint, frequency });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to set frequency');
    return response.data.data;
  },

  async getByProject(projectId: string) {
    const response = await api.get<ApiResponse<any[]>>(`/ams/ams-equipment/project/${projectId}`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch AMS equipment by project');
    return response.data.data;
  },

  async getByAudit(auditId: string) {
    const response = await api.get<ApiResponse<any[]>>(`/ams/ams-equipment/audit/${auditId}`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch AMS equipment by audit');
    return response.data.data;
  },

  async getByStatus(status: string) {
    const response = await api.get<ApiResponse<any[]>>(`/ams/ams-equipment/status/${status}`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch AMS equipment by status');
    return response.data.data;
  },

  async getAssetCheckpoints(assetId: string) {
    const response = await api.get<ApiResponse<any[]>>(`/ams/ams-equipment/asset/${assetId}/checkpoints`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch asset checkpoints');
    return response.data.data;
  },

  async getWithProjectType(equipmentId: string) {
    const response = await api.get<ApiResponse<any>>(`/ams/ams-equipment/${equipmentId}/with-project-type`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch AMS equipment with project type');
    return response.data.data;
  },

  async updateEquipmentStatus(equipmentId: string, status: 'active' | 'inactive') {
    const response = await api.put<ApiResponse<any>>(`/ams/ams-equipment/${equipmentId}/status`, { status });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to update AMS equipment status');
    return response.data.data;
  },

  async createBulkEquipment(payload: { projectId: string; amsProjectAuditId?: string; items: any[] }) {
    const response = await api.post<ApiResponse<any>>('/ams/ams-equipment/bulk', payload);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to create bulk AMS equipment');
    return response.data.data;
  },

  async getEquipmentByProjectAsset(projectAssetId: string) {
    const response = await api.get<ApiResponse<any[]>>(`/ams/ams-equipment/project-asset/${projectAssetId}`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch AMS equipment by project asset');
    return response.data.data;
  },

  async findNonOperationalEquipmentAtLocation(params: {
    projectId: string;
    assetId: string;
    zone?: string;
    block?: string;
    subLocation?: string;
  }) {
    const queryParams = new URLSearchParams();
    queryParams.append('projectId', params.projectId);
    queryParams.append('assetId', params.assetId);
    if (params.zone) queryParams.append('zone', params.zone);
    if (params.block) queryParams.append('block', params.block);
    if (params.subLocation) queryParams.append('subLocation', params.subLocation);

    const response = await api.get<ApiResponse<any[]>>(`/ams/ams-equipment/location/non-operational?${queryParams.toString()}`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch Non-Operational AMS equipment');
    return response.data.data;
  },

  /**
   * Bulk update equipment details (make & model, manufacturer, technical specifications)
   */
  async bulkUpdateEquipment(equipmentIds: string[], updateData: any) {
    const response = await api.put<ApiResponse<any>>('/ams/ams-equipment/bulk-update', {
      equipmentIds,
      updateData
    });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to bulk update AMS equipment');
    return response.data.data;
  },

  /**
   * Upload documents to a single equipment
   */
  async uploadEquipmentDocuments(equipmentId: string, documents: Array<{
    documentName: string;
    documentType: string;
    documentFile: string;
  }>) {
    const response = await api.post<ApiResponse<any>>(`/ams/ams-equipment/${equipmentId}/documents`, {
      documents
    });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to upload documents');
    return response.data.data;
  },

  /**
   * Bulk upload documents to multiple equipment
   */
  async bulkUploadDocuments(equipmentIds: string[], documents: Array<{
    documentName: string;
    documentType: string;
    documentFile: string;
  }>) {
    const response = await api.post<ApiResponse<any>>('/ams/ams-equipment/bulk-documents', {
      equipmentIds,
      documents
    });
    if (!response.data.success) throw new Error(response.data.message || 'Failed to bulk upload documents');
    return response.data.data;
  },

  /**
   * Delete a document from equipment
   */
  async deleteEquipmentDocument(equipmentId: string, documentId: string) {
    const response = await api.delete<ApiResponse<any>>(`/ams/ams-equipment/${equipmentId}/documents/${documentId}`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to delete document');
    return response.data.data;
  }
};

export default amsEquipmentService;
